import React, { type FC } from "react";
import { useNavigate } from "react-router-dom";
import { useWindows } from "@app/overlay";
import { OPEN_FILTER_QUERY_PARAM } from "@app/router/queryParams";
import { CardHeader } from "@shared/ui/card-header";
import { ListItem } from "@src/components/business-list";
import { BusinessList } from "@src/components/business-list";
import { BusinessBoStateValue, BusinessDateValue, BusinessDivisionValue } from "@src/components/business-data-display";
import { useBusinessListQuery } from "@src/hooks/useBusinessListQuery";
import { useUrlFilter } from "@shared/hooks/useUrlFilter";
import { useUrlParamTrigger } from "@shared/hooks/useUrlParamTrigger";
import {
    finishedGoodsReceiptsServices,
    type FinishedGoodsReceipt,
    type FinishedGoodsReceiptExtends,
    type FinishedGoodsReceiptFilter,
} from "../../../../api/finishedGoodsReceipts";
import { stockFinishedGoodsReceiptListWindows } from "./stockFinishedGoodsReceiptList.windows";
import "./StockFinishedGoodsReceiptList.scss";

/** Связи списка ВЗН УП для приемки СГП. */
const FINISHED_GOODS_RECEIPT_LIST_INCLUDES = {
    include: ["Sender", "SenderSection", "Receiver", "ReceiverSection", "Bo"] satisfies Array<
        keyof FinishedGoodsReceiptExtends
    >,
    includeBo: ["State"] satisfies Array<"State">,
} as const;

/** Список ВЗН УП прихода на склад СГП. */
export const StockFinishedGoodsReceiptList: FC = () => {
    const windows = useWindows();
    const navigate = useNavigate();
    const filterState = useUrlFilter<FinishedGoodsReceiptFilter>();

    const list = useBusinessListQuery<FinishedGoodsReceipt, FinishedGoodsReceiptFilter>({
        queryKey: "stock.finishedGoodsReceipt.inplants",
        filter: filterState.filter,
        queryFn: (flt, signal) =>
            finishedGoodsReceiptsServices.load(
                {
                    flt,
                    ...FINISHED_GOODS_RECEIPT_LIST_INCLUDES,
                },
                { signal },
            ),
        getId: (item) => item.Code,
    });

    const openFilterWindow = (): void => {
        stockFinishedGoodsReceiptListWindows.filter.open(windows, {
            initialFilter: filterState.filter,
            onApply: (flt) => {
                filterState.applyFilter(flt, true);
            },
        });
    };

    useUrlParamTrigger(OPEN_FILTER_QUERY_PARAM, () => {
        openFilterWindow();
    });

    return (
        <div className="stock_finished_goods_receipt_list_page">
            <CardHeader
                text="Список ВЗН УП приход на СГП"
                buttons={[
                    {
                        type: "button",
                        text: "Поиск",
                        srcIcon: "/images/common/options.svg",
                        onClick: openFilterWindow,
                    },
                ]}
            />
            <BusinessList list={list} onOpenFilter={openFilterWindow}>
                {list.data.map((item) => (
                    <ListItem
                        key={item.Code}
                        title={`ВЗН №${item.Num}`}
                        props={[
                            { label: "Статус ВЗН", value: <BusinessBoStateValue state={item.Bo?.State} /> },
                            {
                                label: "Отправитель",
                                value: <BusinessDivisionValue division={item.Sender} section={item.SenderSection} />,
                            },
                            {
                                label: "Получатель",
                                value: <BusinessDivisionValue division={item.Receiver} section={item.ReceiverSection} />,
                            },
                            {
                                label: "Дата ВЗН",
                                value: <BusinessDateValue value={item.DocDate} />,
                            },
                        ]}
                        onClick={() => {
                            navigate({ pathname: `${item.Code}` });
                        }}
                    />
                ))}
            </BusinessList>
        </div>
    );
};
